<template>
  <div class="bulk-rate-assign">

    <!-- Selection count bar -->
    <div class="bulk-rate-assign__bar">
      <span class="bulk-rate-assign__count">{{ count }} selected</span>

      <!-- Mixed-org guard -->
      <el-alert
        v-if="!sameOrg"
        type="info"
        :closable="false"
        show-icon
        :title="'Bulk rate change needs a single telco — ' + selectedOrgIds.length + ' selected. Narrow your selection to one telco to continue.'"
        class="bulk-rate-assign__org-alert" />

      <template v-if="sameOrg || selectAllMatching">
        <!-- Mode toggle: override enabled in select-all mode (XOL-340 / API PR #170, verified live) -->
        <el-radio-group
          v-model="applyMode"
          size="small"
          class="bulk-rate-assign__mode"
          aria-label="Apply mode">
          <el-radio-button label="rate">Assign sharer rate</el-radio-button>
          <!-- Wrap in span so el-tooltip fires while the control is gated.
               Tooltip only shows while gated (overrideAvailableInSelectAll=false). -->
          <el-tooltip
            :disabled="!(selectAllMatching && !overrideAvailableInSelectAll)"
            effect="dark"
            placement="top"
            content="Manual override across all matching licensees is coming soon.">
            <span>
              <el-radio-button
                label="override"
                :disabled="selectAllMatching && !overrideAvailableInSelectAll"
                :aria-disabled="selectAllMatching && !overrideAvailableInSelectAll">
                Set manual override
              </el-radio-button>
            </span>
          </el-tooltip>
        </el-radio-group>

        <!-- Rate mode controls -->
        <template v-if="isRateMode">
          <select-sharer-rates
            ref="rateSelect"
            :sharer-id="orgId"
            v-model="rateId"
            :no-form="true"
            placeholder="Choose a rate to apply…"
            class="bulk-rate-assign__picker" />

          <!-- Version picker. In select-all mode it's enabled once the version-pin
               API (XOL-340 / #170) is live; the tooltip only shows while gated. -->
          <el-tooltip
            :disabled="!(selectAllMatching && !versionPinAvailable)"
            effect="dark"
            placement="top"
            content="Pinning a version across all matching licensees is coming soon.">
            <span class="bulk-rate-assign__picker bulk-rate-assign__version">
              <select-rate-versions
                :versions="rateVersions"
                :value="versionId"
                :loading="loadingVersions"
                :disabled="(selectAllMatching && !versionPinAvailable) || !rateId"
                :aria-disabled="selectAllMatching && !versionPinAvailable"
                clearable
                placeholder="Latest (published)"
                @input="onApplyVersionInput"
                style="width: 100%" />
            </span>
          </el-tooltip>

          <!-- XOL-321 Phase B — apply-with-exclusions safety gate.
               Wrap Apply in a <span> + el-tooltip (mirrors the override/version
               "coming soon" pattern) so the tooltip fires on the disabled button.
               :disabled on the tooltip = don't show the tooltip unless
               exclusionsBlockApply is true (i.e. exclusions present + API not ready).
               aria-describedby wires to the tooltip's content id so assistive
               technology reads the reason while Apply is disabled. -->
          <el-tooltip
            :disabled="!exclusionsBlockApply"
            effect="dark"
            placement="top"
            :content="'De-selecting individual records is coming soon. Reset exclusions to apply to all ' + matchCount + ', or use per-page selection instead.'">
            <span>
              <el-button
                type="primary"
                :disabled="!canApply"
                :aria-disabled="!canApply"
                :loading="applying"
                @click="confirmAndApply"
                class="bulk-rate-assign__apply-btn">
                Apply rate to {{ count }}
              </el-button>
            </span>
          </el-tooltip>
        </template>

        <!-- Override mode controls: available in both explicit and select-all modes (XOL-340 live) -->
        <template v-if="isOverrideMode">
          <currency-input
            v-model="overrideAmount"
            :no-form="true"
            label=""
            placeholder="Manual fee amount"
            class="bulk-rate-assign__picker" />

          <el-button
            type="primary"
            :disabled="!canApply"
            :loading="applying"
            @click="confirmAndApply"
            class="bulk-rate-assign__apply-btn">
            Set override on {{ count }}
          </el-button>

          <el-button
            type="danger"
            plain
            :disabled="!canClear"
            :loading="applying"
            @click="confirmAndClear"
            class="bulk-rate-assign__apply-btn">
            Clear override on {{ count }}
          </el-button>
        </template>
      </template>
    </div>

    <!-- Progress indicator: no row counter in select-all mode (async, unknown per-row state) -->
    <div v-if="applying" class="bulk-rate-assign__progress">
      <template v-if="selectAllMatching">Queuing…</template>
      <template v-else>Applying {{ doneCount }} of {{ count }}…</template>
    </div>

    <!-- Result alerts -->
    <template v-if="result">
      <!-- Success / queued alert (both bulk-filter and fan-out modes) -->
      <el-alert
        v-if="isBulkFilterResult || result.applied > 0"
        type="success"
        :closable="false"
        show-icon
        :title="successTitle"
        class="bulk-rate-assign__result-alert" />

      <!-- Error alerts (bulk-filter mode: 422 or generic network/5xx) -->
      <el-alert
        v-if="isBulkFilterError"
        type="error"
        :closable="false"
        show-icon
        :title="result.error"
        class="bulk-rate-assign__result-alert" />

      <!-- Defensive failure list for bulk-filter mode (202 with non-empty failures, normally []) -->
      <el-alert
        v-if="isBulkFilterResult && result.failures && result.failures.length"
        type="warning"
        :closable="false"
        show-icon
        :title="bulkFilterFailureTitle"
        class="bulk-rate-assign__result-alert">
        <div slot="default" class="bulk-rate-assign__failure-list">
          <div
            v-for="(f, idx) in result.failures.slice(0, 50)"
            :key="bulkFilterFailureKey(f, idx)"
            class="bulk-rate-assign__failure-item">
            Licensee {{ f.licensee_id }} — {{ f.reason }}
          </div>
        </div>
      </el-alert>

      <!-- Fan-out failure list (explicit per-row results) -->
      <el-alert
        v-if="result.failed && !isBulkFilterResult"
        type="error"
        :closable="false"
        show-icon
        :title="fanOutFailureTitle"
        class="bulk-rate-assign__result-alert">
        <div slot="default" class="bulk-rate-assign__failure-list">
          <div
            v-for="(f, idx) in result.failures.slice(0, 50)"
            :key="idx"
            class="bulk-rate-assign__failure-item">
            {{ (f.row.organisation && f.row.organisation.name) }} — {{ (f.row.site && f.row.site.siteId) }}
          </div>
        </div>
      </el-alert>
    </template>

  </div>
</template>

<script>

import axios from '@/lib/axiosClient'
import NumberMixins from '@/mixins/NumberMixins'
import SelectSharerRates from '@/components/atoms/selects/SelectSharerRates'
import SelectRateVersions from '@/components/atoms/selects/SelectRateVersions'
import CurrencyInput from '@/components/atoms/inputs/CurrencyInput'

export default {
  name: 'bulk-rate-assign',

  // userName() + getEndpoint() are global app-wide mixin methods (AccessMixins),
  // not PostMixins — so no local mixin is needed here. NumberMixins added for
  // formatMoney in override confirm body (Phase 2).
  mixins: [NumberMixins],

  components: {
    SelectSharerRates,
    SelectRateVersions,
    CurrencyInput
  },

  props: {
    // Scopes the rate picker; when null the picker falls back to the single
    // selected org if sameOrg, otherwise disabled.
    organisationId: {
      type: Number,
      default: null
    },
    // Selected audit rows. Each is expected to carry:
    //   licensee_id, organisation: {id, name}, site: {id, siteId, siteName}
    licenseeRows: {
      type: Array,
      default: function () { return [] }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Phase B: total matched count from the search (XolasTable @total-changed).
    // In select-all mode, count shows this value instead of licenseeRows.length.
    matchCount: {
      type: Number,
      default: null
    },
    // Phase B: true when the user has clicked "Select all N matching" — apply
    // routes through POST /licensees/bulk-rate filter-mode (not fan-out).
    selectAllMatching: {
      type: Boolean,
      default: false
    },
    // Phase B: the searchParams object from BulkRateChange, used as the filter
    // body for POST /licensees/bulk-rate filter-mode.
    filter: {
      type: Object,
      default: function () { return {} }
    },
    // XOL-321 Phase B — select-all-with-exclusions.
    // excludedIds: ids of licensees de-selected while in select-all mode.
    // count subtracts these from matchCount so the display and confirm copy
    // reflect the actual post-exclusion target set.
    // Apply is DISABLED when selectAllMatching && excludedIds.length > 0 &&
    // !excludeIdsApiAvailable (safety gate until brave-kestrel ships exclude_ids).
    excludedIds: {
      type: Array,
      default: function () { return [] }
    }
  },

  data: function () {
    return {
      applyMode: 'rate',
      rateId: null,
      versionId: null,
      rateVersions: [],
      loadingVersions: false,
      overrideAmount: null,
      applying: false,
      doneCount: 0,
      result: null
    }
  },

  computed: {
    // In select-all mode, count reflects the matched set minus any exclusions;
    // guarded with Math.max(0,…) so it never goes negative.
    // In explicit (fan-out) mode, count is the number of checked rows.
    count: function () {
      if (this.selectAllMatching && this.matchCount !== null) {
        return Math.max(0, this.matchCount - this.excludedIds.length)
      }
      return this.licenseeRows.length
    },

    // ENABLED — bulk-rate filter-mode honours mode:override / mode:override-clear
    // (brave-kestrel XOL-340 / #170), verified live: org 982 {mode:override,currentRate}
    // → 202 {queued:299}; {mode:override-clear} → 202 {queued:300}.
    // MUST ship paired with the XOL-340 API merge to main.
    overrideAvailableInSelectAll: function () {
      return true
    },

    // XOL-321 Phase B — apply-with-exclusions safety gate.
    // brave-kestrel: flip to true once POST /licensees/bulk-rate accepts exclude_ids[].
    // While false, Apply is disabled whenever the user has de-selected rows in
    // select-all mode — we must NOT POST a filter-mode request that silently
    // ignores exclusions (money-path correctness bug).
    excludeIdsApiAvailable: function () {
      // ENABLED — POST /licensees/bulk-rate filter-mode honours `exclude_ids[]`
      // (brave-kestrel XOL-347, verified live: org 982 select-all 300 + exclude 3
      // → 202 {queued:297}). This SPA enable MUST ship paired with the XOL-347 API
      // merge to main (they are the two halves of the same feature); do not deploy
      // the SPA ahead of that API change, or exclusions would be silently dropped.
      return true
    },

    // True when the bulk endpoint honours `rate_version_id` in filter-mode (the
    // version pin across all matching). ENABLED — brave-kestrel XOL-340 / #170,
    // verified live: rate 8697 + rate_version_id 980 → 202; a version of another
    // rate → 422 "Rate version does not belong to this rate." Like exclude_ids,
    // this SPA enable MUST ship paired with the XOL-340 API merge to main.
    versionPinAvailable: function () {
      return true
    },

    // True when the Apply button must be disabled AND the tooltip must be shown.
    // Distinct from canApply=false so the template can decide whether to render
    // the tooltip wrapper without duplicating the guard logic.
    exclusionsBlockApply: function () {
      return !!(this.selectAllMatching && this.excludedIds.length > 0 && !this.excludeIdsApiAvailable)
    },

    selectedOrgIds: function () {
      var seen = {}
      var ids = []
      this.licenseeRows.forEach(function (row) {
        var id = row.organisation && row.organisation.id
        if (id && !seen[id]) {
          seen[id] = true
          ids.push(id)
        }
      })
      return ids
    },

    sameOrg: function () {
      return this.selectedOrgIds.length <= 1
    },

    orgId: function () {
      return this.organisationId || this.selectedOrgIds[0] || null
    },

    canApply: function () {
      // XOL-321 Phase B — safety gate: NEVER allow apply when exclusions are
      // present and the exclude_ids API param is not yet available. This prevents
      // a filter-mode POST that would silently apply to the FULL matched set
      // (ignoring exclusions) — a money-path correctness bug.
      if (this.exclusionsBlockApply) return false

      // In select-all mode, sameOrg is inherent (the filter scopes to organisation_id)
      var base = this.count > 0 && !this.applying && !this.disabled
      if (!this.selectAllMatching) {
        base = base && this.sameOrg
      }
      // Override is now available in select-all mode (XOL-340 live).
      var effectiveMode = this.applyMode
      if (effectiveMode === 'rate') {
        return base && !!this.rateId
      }
      // override-set — NOT gated on overrideAmount: CurrencyInput only commits its
      // value on blur (@change), so if the button were disabled until the amount
      // is non-zero, the user's first click (which blurs the field) would land on a
      // still-disabled button and do nothing. Keep it enabled and validate the
      // amount in confirmAndApply (which runs after the blur commits the value).
      return base
    },

    canClear: function () {
      return this.count > 0 && this.sameOrg && !this.applying && !this.disabled
    },

    // Template helpers to avoid string comparisons with single quotes inside
    // double-quoted attribute values (causes vue-jest template compilation errors
    // when the vue-jest template compiler tokenizes attribute values).
    // Override is now reachable in select-all mode (XOL-340 live); the selectAllMatching
    // watcher still snaps applyMode='rate' on entry so rate is the default.
    isRateMode: function () {
      return this.applyMode === 'rate'
    },

    isOverrideMode: function () {
      return this.applyMode === 'override'
    },

    isBulkFilterResult: function () {
      return !!(this.result && this.result.mode === 'bulk-filter')
    },

    isBulkFilterError: function () {
      return !!(this.result && (this.result.mode === 'bulk-filter-error-422' || this.result.mode === 'bulk-filter-error'))
    },

    bulkFilterFailureTitle: function () {
      if (!this.result || !this.result.failures) return ''
      var n = this.result.failures.length
      return n + ' licensee' + (n === 1 ? '' : 's') + " couldn't be queued"
    },

    fanOutFailureTitle: function () {
      if (!this.result) return ''
      return this.result.failed + " couldn't be applied"
    },

    successTitle: function () {
      if (!this.result) return ''
      // Phase B: bulk-filter mode — always-202 async result; mode-aware title
      if (this.result.mode === 'bulk-filter') {
        var q = this.result.queued
        var s = q === 1 ? '' : 's'
        var bm = this.result.bulkMode
        if (bm === 'rate') {
          return 'Queued ' + q + ' rate change' + s + ' — fees recalculating in the background.'
        }
        if (bm === 'override') {
          return 'Queued ' + q + ' override' + s + ' — fees recalculating in the background.'
        }
        if (bm === 'override-clear') {
          return 'Queued ' + q + ' override removal' + s + ' — fees recalculating in the background.'
        }
        // Fallback (no bulkMode — legacy callers)
        return 'Queued ' + q + ' — fees recalculating in the background.'
      }
      var n = this.result.applied
      if (this.result.mode === 'rate') {
        return 'Applied ' + n + ' rate change' + (n === 1 ? '' : 's') + '.'
      }
      if (this.result.mode === 'override-set') {
        return 'Applied ' + n + ' override' + (n === 1 ? '' : 's') + '.'
      }
      if (this.result.mode === 'override-clear') {
        return 'Cleared ' + n + ' override' + (n === 1 ? '' : 's') + '.'
      }
      return 'Applied ' + n + ' change' + (n === 1 ? '' : 's') + '.'
    }
  },

  methods: {
    // Key helper for the defensive failure list in bulk-filter mode.
    // Avoids inline string concatenation with single quotes in the template.
    bulkFilterFailureKey: function (f, idx) {
      return String(f.licensee_id || idx) + '-' + String(f.reason || '')
    },

    // Resolve the display name for the chosen rate, falling back gracefully.
    resolveRateName: function () {
      var rateSelect = this.$refs.rateSelect
      if (rateSelect && rateSelect.options) {
        var id = this.rateId
        var match = rateSelect.options.find(function (o) { return o.id === id })
        if (match) return match.name
      }
      return 'the selected rate'
    },

    // Resolve the version suffix string for the rate-assign confirm body.
    resolveVersionSuffix: function () {
      if (!this.versionId) return ' (latest version)'
      var vid = this.versionId
      var match = this.rateVersions.find(function (v) { return v.id === vid })
      if (match && match.version_no !== undefined) {
        return ' (version v' + match.version_no + ')'
      }
      return ' (the pinned version)'
    },

    onApplyVersionInput: function (val) {
      this.versionId = val
    },

    // On rate change: clear version, load versions, auto-pin is_current.
    onRateChange: function (rateId) {
      this.versionId = null
      this.rateVersions = []
      if (!rateId) return
      var self = this
      self.loadingVersions = true
      axios.get(self.getEndpoint('rates/' + rateId + '/versions'))
        .then(function (resp) {
          var data = (resp.data && resp.data.data) ? resp.data.data : (resp.data || [])
          self.rateVersions = Array.isArray(data) ? data : []
          // Auto-pin is_current
          var current = self.rateVersions.find(function (v) { return v.is_current })
          self.versionId = current ? current.id : null
          self.loadingVersions = false
        })
        .catch(function () {
          self.rateVersions = []
          self.versionId = null
          self.loadingVersions = false
        })
    },

    confirmAndApply: function () {
      // Override amount commits on the input's change (blur). Clicking Apply blurs
      // the field first, so the just-typed value is in `overrideAmount` by now.
      // Validate it BEFORE the canApply gate so an empty/zero amount shows guidance
      // rather than silently proceeding.
      if (this.applyMode === 'override' && !(Number(this.overrideAmount) > 0)) {
        this.$message({ type: 'warning', message: 'Enter a manual fee greater than 0 before applying the override.' })
        return
      }

      if (!this.canApply) return

      var self = this

      // Phase B — select-all mode: route to filter-mode POST /licensees/bulk-rate
      if (this.selectAllMatching) {
        var filterOrgName = (this.filter && this.filter.organisation_id)
          ? this.filter.organisation_id
          : ''
        // Try to resolve the org name from the first checked row (may be empty in
        // pure select-all with no row checked, but the banner requires at least the
        // page to be fully selected so licenseeRows[0] is reliable).
        var orgNameForAll = (self.licenseeRows[0] && self.licenseeRows[0].organisation && self.licenseeRows[0].organisation.name) || String(filterOrgName)
        var N = this.count
        var bulkMessage, bulkTitle, bulkConfirmText

        if (this.applyMode === 'override') {
          // Select-all override: mirror explicit override copy for all-matching
          var overrideAmt = this.formatMoney(this.overrideAmount, 2)
          bulkTitle = 'Set manual override'
          bulkConfirmText = 'Set override'
          bulkMessage = 'Set a manual fee of <strong>' + overrideAmt + '</strong> on all <strong>' + N + '</strong> matching licensees for <strong>' + orgNameForAll + '</strong>?' +
            '<br/>This locks each fee at the manual amount; the scheduled rate recalculation skips these licensees until the override is cleared.'
        } else {
          // Rate mode (default)
          var rateName = this.resolveRateName()
          bulkTitle = 'Apply rate change'
          bulkConfirmText = 'Apply rate change'
          bulkMessage = 'Apply <strong>' + rateName + '</strong> to all <strong>' + N + '</strong> matching licensees for <strong>' + orgNameForAll + '</strong>?' +
            '<br/>This replaces the current rate on every matching licensee and triggers a fee recalculation — fees recompute in the background.'
        }

        var selectAllMode = this.applyMode
        this.$confirm(bulkMessage, bulkTitle, {
          confirmButtonText: bulkConfirmText,
          cancelButtonText: 'Cancel',
          dangerouslyUseHTMLString: true,
          center: true,
          type: 'warning'
        }).then(function () {
          self.runBulkFilter(selectAllMode)
        }).catch(function () {
          // Confirm cancelled — no-op
        })
        return
      }

      // Explicit (fan-out) path
      var orgName = (this.licenseeRows[0] && this.licenseeRows[0].organisation && this.licenseeRows[0].organisation.name) || ''
      var count = this.count
      var message, title, confirmText

      if (this.applyMode === 'rate') {
        var rateName2 = this.resolveRateName()
        var versionSuffix = this.resolveVersionSuffix()
        title = 'Apply rate change'
        confirmText = 'Apply rate change'
        message = 'Apply <strong>' + rateName2 + '</strong>' + versionSuffix +
          ' to <strong>' + orgName + '</strong> (' + count + ' licensee' + (count === 1 ? '' : 's') + ')?' +
          '<br/>This replaces the current rate on every selected licensee and triggers a fee recalculation — fees recompute in the background.'
      } else {
        // override-set — formatMoney prepends settings.currency automatically
        var amount = this.formatMoney(this.overrideAmount, 2)
        title = 'Set manual override'
        confirmText = 'Set override'
        message = 'Set a manual fee of <strong>' + amount + '</strong> on <strong>' + orgName + '</strong> (' +
          count + ' licensee' + (count === 1 ? '' : 's') + ')?' +
          '<br/>This locks each fee at the manual amount; the scheduled rate recalculation skips these licensees until the override is cleared.'
      }

      this.$confirm(message, title, {
        confirmButtonText: confirmText,
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        center: true,
        type: 'warning'
      }).then(function () {
        self.runFanOut(self.applyMode === 'rate' ? 'rate' : 'override-set')
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    confirmAndClear: function () {
      if (!this.canClear) return

      var self = this
      var count = this.count
      var message, title, confirmText

      if (this.selectAllMatching) {
        // Select-all clear: route through filter-mode POST
        var filterOrgName = (this.filter && this.filter.organisation_id)
          ? this.filter.organisation_id
          : ''
        var orgNameForClear = (self.licenseeRows[0] && self.licenseeRows[0].organisation && self.licenseeRows[0].organisation.name) || String(filterOrgName)
        title = 'Clear override'
        confirmText = 'Clear override'
        message = 'Clear the manual override on all <strong>' + count + '</strong> matching licensees for <strong>' + orgNameForClear + '</strong>?' +
          '<br/>This returns them to the calculated (automatic) rate; fees recompute in the background — this may take a moment.'

        this.$confirm(message, title, {
          confirmButtonText: confirmText,
          cancelButtonText: 'Cancel',
          dangerouslyUseHTMLString: true,
          center: true,
          type: 'warning'
        }).then(function () {
          self.runBulkFilter('override-clear')
        }).catch(function () {
          // Confirm cancelled — no-op
        })
        return
      }

      // Explicit (fan-out) path
      var orgName = (this.licenseeRows[0] && this.licenseeRows[0].organisation && this.licenseeRows[0].organisation.name) || ''
      message = 'Clear the manual override on <strong>' + orgName + '</strong> (' +
        count + ' licensee' + (count === 1 ? '' : 's') + ')?' +
        '<br/>This returns ' + count + ' licensee' + (count === 1 ? '' : 's') +
        ' to the calculated (automatic) rate; fees recompute in the background — this may take a moment.'

      this.$confirm(message, 'Clear override', {
        confirmButtonText: 'Clear override',
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        center: true,
        type: 'warning'
      }).then(function () {
        self.runFanOut('override-clear')
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    // Build the per-row request based on mode.
    requestForRow: function (row, mode) {
      var self = this
      if (mode === 'rate') {
        var payload = {
          rate_id: self.rateId,
          site_id: row.site && row.site.id,
          organisation_id: row.organisation && row.organisation.id,
          updated_by: self.userName()
        }
        // XOL-497: OMIT the key when no version is picked — do not send an
        // explicit null. This path PUTs to /licensees/{id}, the SINGLE-licensee
        // endpoint, where API XOL-507 refuses an explicit null with a 422 on a
        // rate change. The filter-mode POST below goes to the BULK endpoint,
        // whose $request->filled() treats null and '' as absent, so a null is
        // harmless there. Two endpoints, opposite semantics, one picker feeding
        // both — this branch previously sent `self.versionId || null`
        // unconditionally.
        //
        // Before #252 that succeeded by silently creating the rated-but-unpinned
        // row XOL-507 exists to prevent, so the 422 is correct and the payload
        // was wrong. Omitting makes the API auto-pin is_current, which is what
        // the confirm's "(latest version)" already promises.
        if (self.versionId) {
          payload.rate_version_id = self.versionId
        }
        return axios.put(self.getEndpoint('/licensees/' + row.licensee_id), payload)
      }
      if (mode === 'override-set') {
        return axios.put(self.getEndpoint('/licensees/' + row.licensee_id + '/rate-override'), {
          currentRate: self.overrideAmount
        })
      }
      // override-clear
      return axios.delete(self.getEndpoint('/licensees/' + row.licensee_id + '/rate-override'))
    },

    runFanOut: function (mode) {
      var self = this
      var rows = this.licenseeRows.slice()
      var succeeded = []
      var failed = []

      this.applying = true
      this.doneCount = 0
      this.result = null

      // Sequential fan-out with bounded concurrency = 1 (deterministic, avoids
      // flooding the server). The plan allows ≤ 3; sequential keeps error order
      // predictable and is simplest to reason about.
      var idx = 0

      function next () {
        if (idx >= rows.length) {
          self.result = {
            applied: succeeded.length,
            failed: failed.length,
            failures: failed,
            mode: mode
          }
          self.applying = false
          if (succeeded.length > 0) {
            self.$emit('applied', self.result)
            if (mode === 'rate') self.rateId = null
            if (mode === 'override-set') self.overrideAmount = null
          }
          return
        }

        var row = rows[idx]
        idx++

        self.requestForRow(row, mode)
          .then(function () {
            succeeded.push(row)
            self.doneCount++
            next()
          })
          .catch(function (err) {
            failed.push({ row: row, error: err })
            self.doneCount++
            next()
          })
      }

      next()
    },

    // Phase B — filter-mode apply: single POST /api/licensees/bulk-rate.
    // mode: 'rate' (default) | 'override' | 'override-clear'
    // Payload rules per mode:
    //   rate: {rate_id, organisation_id, filter} + rate_version_id (when pinned) + exclude_ids (when non-empty)
    //   override: {mode:'override', currentRate, organisation_id, filter} + exclude_ids (when non-empty); NO rate_id/version
    //   override-clear: {mode:'override-clear', organisation_id, filter} + exclude_ids (when non-empty); NO rate_id/currentRate/version
    // organisation_id goes at top level AND stays inside filter (see note below).
    // axios 0.15.3 resolves (does NOT reject) on 202 — handle in .then. 422 lands in .catch.
    runBulkFilter: function (mode) {
      var self = this
      mode = mode || 'rate'

      // Build the filter payload. organisation_id goes at the TOP LEVEL (the
      // controller's required_with:filter + rate-belongs-to-org check read it
      // there). It also STAYS inside `filter`: the endpoint's `filter` rule is
      // `required_without:licensee_ids|array`, and Laravel's required treats an
      // empty array as absent — so a telco-only select-all (no other filters)
      // would 422 with an empty filter. Keeping organisation_id in `filter`
      // guarantees it is non-empty; applyLicenseeFilters has no organisation_id
      // handler so it is a no-op there (org scoping is separate). The filter then
      // mirrors exactly what /licensees/search received.
      var filter = Object.assign({}, this.filter)
      var orgId = this.filter.organisation_id

      var payload

      if (mode === 'override') {
        // Override mode: send mode + currentRate; NO rate_id / rate_version_id
        payload = {
          mode: 'override',
          currentRate: Number(this.overrideAmount),
          organisation_id: orgId,
          filter: filter
        }
      } else if (mode === 'override-clear') {
        // Override-clear mode: just mode; NO rate_id / currentRate / version
        payload = {
          mode: 'override-clear',
          organisation_id: orgId,
          filter: filter
        }
      } else {
        // Rate mode (default)
        payload = {
          rate_id: this.rateId,
          organisation_id: orgId,
          filter: filter
        }
        // Version pin (XOL-340 / #170): send the chosen version when one is picked.
        // Nothing picked → OMIT the key, so the API auto-pins the rate's current
        // published version.
        //
        // XOL-497 fold-in (Carlos, 2026-09-13): the placeholder used to read
        // "Latest (unversioned)", naming a state the product does not allow —
        // a licensee is always pinned to a PUBLISHED version. The label was
        // wrong; the behaviour was already right, and the distinction matters
        // if anyone revisits this.
        //
        // Unlike FinanceSharer's version select, `clearable` STAYS here.
        // Clearing this control does not unpin anyone: it omits the key, and
        // the API then pins `is_current` (RateVersion::currentForRate) or
        // fails the row with `rate_not_published` when the rate has no
        // published version. That is a legitimate choice — "give them whatever
        // is current" — and it is the default. FinanceSharer differed because
        // clearing there SENT an explicit null on a rate change, which API
        // XOL-507 now rejects with a 422; that is why clearable was removed
        // from that one and not from this one.
        //
        // NOTE: that reasoning holds for THIS branch only. requestForRow()
        // PUTs to the single-licensee endpoint and had to be fixed separately
        // — see the comment there. "Clearable is safe on the bulk picker" is
        // true of the endpoint, not of every request the screen makes.
        if (this.versionPinAvailable && this.versionId) {
          payload.rate_version_id = this.versionId
        }
      }

      // XOL-321 Phase B — include exclude_ids only when non-empty. Applies to all modes.
      if (this.excludedIds.length) {
        payload.exclude_ids = this.excludedIds.slice()
      }

      this.applying = true
      this.result = null

      axios.post(self.getEndpoint('/licensees/bulk-rate'), payload)
        .then(function (resp) {
          // Always 202 — axios resolves here (default validateStatus: 200-299)
          self.result = {
            mode: 'bulk-filter',
            bulkMode: mode,
            queued: resp.data.queued,
            failures: resp.data.failures || []
          }
          self.applying = false
          // Reset the relevant input per mode
          if (mode === 'rate') self.rateId = null
          if (mode === 'override') self.overrideAmount = null
          // override-clear: no input to reset
          self.$emit('applied', self.result)
        })
        .catch(function (err) {
          self.applying = false
          var data = (err.response && err.response.data) || {}
          if (err.response && err.response.status === 422) {
            // Guards that return {error: …}: the rate-not-in-organisation case gets
            // a friendly telco message; the rate-version-mismatch case (and any
            // other {error}) is surfaced verbatim — it's already readable and the
            // telco copy would mislead. Validation errors fall back to {message}.
            var serverError = data.error || ''
            var message
            if (/organisation/i.test(serverError)) {
              message = "That rate isn't available for this telco. Pick a rate that belongs to the selected telco and try again."
            } else if (serverError) {
              message = serverError
            } else {
              message = data.message || "Couldn't queue the rate change. Check your filters and try again."
            }
            self.result = {
              mode: 'bulk-filter-error-422',
              error: message
            }
          } else {
            self.result = {
              mode: 'bulk-filter-error',
              error: "Couldn't queue the rate change. Try again in a moment."
            }
          }
        })
    }
  },

  watch: {
    // Switching mode clears the result alert so a stale banner does not
    // sit under the new mode's controls.
    applyMode: function () {
      this.result = null
    },

    rateId: function (val, oldVal) {
      if (val === oldVal) return
      this.onRateChange(val)
    },

    // Entering select-all snaps to rate mode as the safe default.
    // Override is now enabled in select-all (XOL-340 live), but defaulting to rate
    // is the least-surprising behaviour when the user first clicks "Select all N".
    // The user can switch to override after entering select-all mode.
    selectAllMatching: function (on) {
      if (on) this.applyMode = 'rate'
    }
  }
}
</script>

<style scoped>
.bulk-rate-assign {
  background: #f5f7fa;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 12px;
}

.bulk-rate-assign__bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.bulk-rate-assign__count {
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}

.bulk-rate-assign__org-alert {
  flex: 1;
  min-width: 260px;
}

.bulk-rate-assign__mode {
  flex: 0 0 auto;
}

.bulk-rate-assign__picker {
  flex: 1;
  min-width: 200px;
  max-width: 320px;
}

.bulk-rate-assign__version {
  flex: 1;
  min-width: 200px;
  max-width: 320px;
}

.bulk-rate-assign__apply-btn {
  white-space: nowrap;
}

.bulk-rate-assign__progress {
  margin-top: 8px;
  color: #909399;
  font-size: 13px;
}

.bulk-rate-assign__result-alert {
  margin-top: 10px;
}

.bulk-rate-assign__failure-list {
  margin-top: 4px;
  max-height: 160px;
  overflow-y: auto;
  font-size: 12px;
}

.bulk-rate-assign__failure-item {
  padding: 2px 0;
  color: #606266;
}
</style>
